import React, { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import { Cleaner } from '#component/shipment/form/helper';
import { processAfterSubmitAction, setCargo, setPristine } from '#action/shipment/form';
import { reduxFormResolver } from '#helper/rhf-resolver';
import { useStepSubmit } from '#component/shipment/form/use-step-submit';
import validate from './form/validate';
import Form from './form/form';
import { uuidv4 } from '#helper';

const handleFormSubmit = (shipment, dispatch, isCopiedShipment) => async values => {
  const formattedCargo = Cleaner.cleanArray([...values.cargo]).map(cargo => {
    if (typeof cargo.id === 'string' || isCopiedShipment) {
      delete cargo.id;
    }
    if (cargo.quantity != null && cargo.quantity !== '') {
      cargo.quantity = Number(cargo.quantity);
    }
    if (cargo.length != null && cargo.length !== '') {
      cargo.length = Number(cargo.length);
    }
    if (cargo.width != null && cargo.width !== '') {
      cargo.width = Number(cargo.width);
    }
    if (cargo.height != null && cargo.height !== '') {
      cargo.height = Number(cargo.height);
    }
    if (cargo.weight != null && cargo.weight !== '') {
      cargo.weight = Number(cargo.weight);
    }
    return cargo;
  });

  dispatch(setCargo(formattedCargo, shipment));
  await dispatch(processAfterSubmitAction());
};

const deviceToCargo = (d, cargo = {}) => {
  if (typeof d !== 'object' || d === null) {
    return {
      label: `${d}`,
      title: `${d}`,
      value: d,
      id: d,
      uid: `${d}`,
      showAlertIcon: false,
      alertLabel: 'Switch on/off'
    };
  }

  const id = d.id ?? d.value;
  const label = d.label || d.title || d.uid || `${id}`;

  return {
    label,
    title: d.title || label,
    value: id,
    id,
    uid: d.uid || label,
    disabledBy: d.disabledBy,
    color: d.color ? d.color : null,
    alertOn: !!d.projectAlertId,
    showAlertIcon: cargo.projectAlertId,
    alertLabel: 'Switch on/off',
    type: d.type
  };
};

const cargoItemDefaultValues = {
  projectAlertId: null,
  devices: [],
  goods: null,
  container: null,
  length: null,
  width: null,
  height: null,
  weight: null,
  quantity: 1,
  fragile: 0,
  hazmat: 0,
  description: null,
  id: uuidv4()
};

const CargoForm = props => {
  const { initialValues, onSubmit, editable, registerStepSubmit, form } = props;
  const dispatch = useDispatch();

  const methods = useForm({
    resolver: reduxFormResolver((values, context) => validate(values, { ...props, ...context })),
    defaultValues: initialValues || { cargo: [] },
    mode: 'onChange'
  });

  const { reset, formState: { isDirty } } = methods;
  const prevInitialRef = React.useRef(null);

  useEffect(() => {
    if (!initialValues) return;
    const serialized = JSON.stringify(initialValues);
    if (prevInitialRef.current === serialized) return;
    prevInitialRef.current = serialized;
    if (!isDirty) {
      reset(initialValues);
    }
  }, [initialValues, reset, isDirty]);

  useEffect(() => {
    if (form) {
      dispatch(setPristine(form, !isDirty));
    }
  }, [dispatch, form, isDirty]);

  const onFormSubmit = async values => {
    if (editable) {
      await onSubmit(values);
    } else {
      dispatch(processAfterSubmitAction());
    }
  };

  useStepSubmit(registerStepSubmit, methods, onFormSubmit);

  return (
    <FormProvider {...methods}>
      <Form {...props} onFormSubmit={onFormSubmit} />
    </FormProvider>
  );
};

const mapStateToProps = (state, props) => {
  const { shipment, account, shipmentForm } = state;
  const { dispatch, editable, isCopiedShipment } = props;
  const defaultDevs = (shipment.defaultDevices?.length ?
    shipment.defaultDevices : shipment.devices) ?? [];
  const devicesWithoutCargo = defaultDevs.reduce((a, d) => {
    if (!d.cargoId) {
      a.push(deviceToCargo(d));
    }
    return a;
  }, []);

  const cargoItems = (shipmentForm.cargo?.length ? shipmentForm.cargo : shipment.cargo) ?? [];
  const initialValues = { cargo: [{ ...cargoItemDefaultValues, devices: devicesWithoutCargo }] };

  if (cargoItems.length) {
    initialValues.cargo = cargoItems.map((c, index) => ({
      ...cargoItemDefaultValues,
      ...c,
      devices: Array.isArray(c.devices) && c.devices.length ?
        c.devices.map(d => {
          if (typeof d === 'object' && d !== null && (d.title || d.label || d.uid)) {
            return deviceToCargo(d, c);
          }
          const idToFind = typeof d === 'object' ? d.id : d;
          const found = defaultDevs.find(dev => dev.id === idToFind);
          return deviceToCargo(found || d, c);
        }) :
        defaultDevs.reduce((a, d) => {
          if (d.cargoId === c.id || (index === 0 && !d.cargoId)) {
            a.push(deviceToCargo(d, c));
          }
          return a;
        }, [])
    }));
  }

  return {
    account,
    shipment,
    initialValues,
    editable,
    isCopiedShipment,
    onSubmit: handleFormSubmit(shipment, dispatch, isCopiedShipment)
  };
};

export default connect(mapStateToProps)(CargoForm);
